













































































































import { mixins } from "vue-class-component";
import { Component, Inject, Prop } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import FormField from "@/lib/components/FormField.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import IdGenerator from "@/lib/services/id-generator";
import dataStore from "@/store";
import Product from "@/modules/plushie/product.model";
import PlushieStore from "@/modules/plushie/store";

import FactoryRushCapacityProductGroupMember from "../../factory-rush-capacity-product-group-member.model";
import FactoryRushCapacityProductGroup from "../../factory-rush-capacity-product-group.model";
import FactoryRushCapacityStore from "../../store";

@Component({
  components: {
    FormErrors,
    FormField,
    Multiselect,
    SubmitButton,
  },
})
export default class FactoryRushCapacityProductGroupForm extends mixins(
  GeneralFormMixin
) {
  @Prop()
  public readonly group?: FactoryRushCapacityProductGroup;

  @Prop({ default: false })
  public readonly canManage!: boolean;

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  public name = "";
  public selectedProduct: Product | null = null;

  private fFactoryRushCapacityStore: FactoryRushCapacityStore;
  private fInitialProductIds: string[] = [];
  private fPlushieStore: PlushieStore;
  private fSavedGroup?: FactoryRushCapacityProductGroup;
  private fStagedProducts: Product[] = [];

  get eligibleProducts(): Product[] {
    const stagedProductIds = this.fStagedProducts.map((product) => product.id);

    return this.fPlushieStore.productsList.filter((product) => {
      if (stagedProductIds.includes(product.id)) {
        return false;
      }

      const member = this.fFactoryRushCapacityStore.getFactoryRushCapacityProductGroupMemberByProductId(
        product.id
      );

      if (!member) {
        return true;
      }

      return (
        this.fFactoryRushCapacityStore.getFactoryRushCapacityProductIdsByProductGroupId(
          member.productGroup
        ).length === 1
      );
    });
  }

  get stagedProducts(): Product[] {
    return [...this.fStagedProducts].sort((left, right) =>
      left.name.toLowerCase().localeCompare(right.name.toLowerCase())
    );
  }

  constructor() {
    super();

    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );
    this.fPlushieStore = getModule(PlushieStore, dataStore);
  }

  public addProduct(): void {
    const selectedProduct = this.selectedProduct;

    if (!selectedProduct || this.isDisabled) {
      return;
    }

    if (
      !this.eligibleProducts.some(
        (product) => product.id === selectedProduct.id
      )
    ) {
      return;
    }

    this.fStagedProducts.push(selectedProduct);
    this.selectedProduct = null;
  }

  public deleteProduct(productId: string): void {
    if (this.isDisabled) {
      return;
    }

    this.fStagedProducts = this.fStagedProducts.filter(
      (product) => product.id !== productId
    );
  }

  public onCancelClick(): void {
    if (this.isDisabled) {
      return;
    }

    this.$emit("cancel");
  }

  protected created(): void {
    this.initializeForm();
  }

  protected async performSubmit(): Promise<void> {
    if (!this.canManage) {
      throw new Error("You do not have enough permissions");
    }

    const addedProductIds = this.getAddedProductIds();
    const removedProductIds = this.getRemovedProductIds();

    if (
      removedProductIds.length &&
      !confirm(
        "Remove the selected Products from this group? Each removed Product becomes unconfigured without copied capacity or a replacement group."
      )
    ) {
      return;
    }

    const savedGroup = await this.saveGroupIfNeeded();

    try {
      for (const productId of removedProductIds) {
        await this.fFactoryRushCapacityStore.removeFactoryRushCapacityProductGroupMemberByProductId(
          productId
        );
      }

      for (const productId of addedProductIds) {
        await this.fFactoryRushCapacityStore.addFactoryRushCapacityProductGroupMember(
          new FactoryRushCapacityProductGroupMember(productId, savedGroup.id)
        );
      }

      await this.refreshConfirmedState(savedGroup.id);
    } catch (error) {
      await this.recoverFromMembershipSaveFailure(savedGroup.id, error);
      return;
    }

    this.$emit("saved", savedGroup);
  }

  private createGroup(): FactoryRushCapacityProductGroup {
    const currentGroup = this.fSavedGroup || this.group;
    const group = new FactoryRushCapacityProductGroup(
      currentGroup ? currentGroup.id : this.fIdGenerator.getId(),
      this.name.trim()
    );

    group.isNew = currentGroup === undefined;

    return group;
  }

  private getAddedProductIds(): string[] {
    return this.fStagedProducts
      .map((product) => product.id.toString())
      .filter((productId) => !this.fInitialProductIds.includes(productId));
  }

  private getRemovedProductIds(): string[] {
    const stagedProductIds = this.fStagedProducts.map((product) =>
      product.id.toString()
    );

    return this.fInitialProductIds.filter(
      (productId) => !stagedProductIds.includes(productId)
    );
  }

  private initializeForm(): void {
    const currentGroup = this.fSavedGroup || this.group;
    this.name = currentGroup ? currentGroup.name : "";

    if (!currentGroup) {
      this.fInitialProductIds = [];
      this.fStagedProducts = [];
      return;
    }

    this.fInitialProductIds = this.fFactoryRushCapacityStore.getFactoryRushCapacityProductIdsByProductGroupId(
      currentGroup.id
    );
    this.fStagedProducts = this.fInitialProductIds
      .map((productId) => this.fPlushieStore.getProductById(productId))
      .filter((product): product is Product => product !== undefined);
  }

  private async recoverFromMembershipSaveFailure(
    groupId: string,
    saveError: unknown
  ): Promise<void> {
    const errors = this.fErrorConverterService.describeError(saveError);

    try {
      await this.refreshConfirmedState(groupId);
    } catch (refreshError) {
      errors.push(...this.fErrorConverterService.describeError(refreshError));
    }

    this.submitErrors = errors;
  }

  private async refreshConfirmedState(groupId: string): Promise<void> {
    this.fFactoryRushCapacityStore.clearFactoryRushCapacitySlotCache();
    await this.fFactoryRushCapacityStore.reloadFactoryRushCapacityProductGroupData();

    const confirmedGroup = this.fFactoryRushCapacityStore.getFactoryRushCapacityProductGroupById(
      groupId
    );

    if (confirmedGroup) {
      this.fSavedGroup = confirmedGroup;
    }

    this.fInitialProductIds = this.fFactoryRushCapacityStore.getFactoryRushCapacityProductIdsByProductGroupId(
      groupId
    );
  }

  private async saveGroupIfNeeded(): Promise<FactoryRushCapacityProductGroup> {
    const currentGroup = this.fSavedGroup || this.group;

    if (currentGroup && currentGroup.name === this.name.trim()) {
      return currentGroup;
    }

    const savedGroup = await this.fFactoryRushCapacityStore.saveFactoryRushCapacityProductGroup(
      this.createGroup()
    );
    this.fSavedGroup = savedGroup;

    return savedGroup;
  }
}
